import { Box, IconButton, Modal, Typography, FormControl, Select, MenuItem } from '@mui/material';
import React from 'react';
import './SubjectRejectModal.css';
import closeIcon from '../../../../../assets/Admin/RequestImages/close-icon.svg';
import rejectIcon from '../../../../../assets/Admin/RequestImages/reject-icon.svg';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useState } from 'react';
import { axiosInstance } from '../../../../../utils/tokenUtils';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 300,
  bgcolor: 'background.paper',
  boxShadow: 24,
};

const customToast = message => {
  toast.custom(
    t => (
      <div className="flex bg-[#F1FFF4] shadow-lg rounded-lg mt-[40px]">
        <div className="flex-1 w-full p-4 flex items-center gap-4">
          <div className="close-icon-container">
            <CloseIcon style={{ color: '#FFF', width: '14px', height: '14px' }} />
          </div>
          <p className="text-sm font-medium text-gray-900">{message}</p>
          <div
            onClick={() => toast.dismiss(t.id)}
            className="w-[14px] h-[14px] cursor-pointer rounded-full flex items-center justify-center border border-[#D5DEE3] p-2"
            role="button"
            tabIndex={0}
          >
            <CloseIcon style={{ color: '#D5DEE3', width: '14px', height: '14px' }} />
          </div>
        </div>
      </div>
    ),
    {
      duration: 3000,
      position: 'top-right',
      id: 'custom-toast',
    }
  );
};

// Predefined rejection reasons
const REJECTION_REASONS = [
  'Insufficient qualifications for the subject',
  'Subject already has enough instructors',
  'Experience level does not match requirements',
  'Portfolio/credentials incomplete',
  'Subject expertise not adequately demonstrated',
  'Application submitted incomplete',
  'Does not meet minimum requirements',
  'Other institutional reasons',
];

const SubjectRejectModal = ({ setOpenRejectModal, rowId, teacherName }) => {
  const navigate = useNavigate();
  const [reason, setReason] = useState('');
  const [selectedPresetReason, setSelectedPresetReason] = useState('');

  const handleClose = () => {
    setOpenRejectModal(false);
  };

  const handlePresetReasonChange = event => {
    const selectedReason = event.target.value;
    setSelectedPresetReason(selectedReason);
    if (selectedReason) {
      setReason(selectedReason);
    }
  };

  const handleTextChange = e => {
    setReason(e.target.value);
    // If user types manually, clear the preset selection
    if (e.target.value !== selectedPresetReason) {
      setSelectedPresetReason('');
    }
  };

  const handleReject = async () => {
    try {
      if (!reason.trim()) {
        toast.error('Rejection reason is required');
        return;
      }
      const res = await axiosInstance.patch(`/admin/subjects/requests/${rowId}`, {
        status: 'rejected',
        reason: reason.trim(),
      });
      if (res?.status === 200) {
        setOpenRejectModal(false);
        navigate('/admin/subject-request');
        customToast(`${teacherName} request rejected`);
      }
    } catch (error) {
      console.log(error);
      toast.error('Failed to reject application');
    }
  };

  return (
    <Modal
      open={true}
      // onClose={handleClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box
        sx={{
          ...style,
          width: '500px',
          maxHeight: '90vh',
          fontFamily: 'Poppins',
          borderRadius: '12px',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box className="flex items-center justify-between px-6 py-4 border-b border-[#D5DEE3]">
          <Typography className="reject-application-text">Reject Application</Typography>
          <IconButton onClick={handleClose}>
            <img src={closeIcon} alt="close" />
          </IconButton>
        </Box>
        <Box className="flex flex-col items-center justify-center text-center gap-4 px-[8%] py-6 border-b border-[#D5DEE3]">
          <img src={rejectIcon} alt="reject" />
          <Typography className="reject-application-text">
            Are you sure you want to reject the application? This can't be undone.
          </Typography>

          <Box className="flex flex-col justify-start items-start gap-4 w-[100%]">
            {/* Preset Reasons Dropdown */}
            <Box className="flex flex-col justify-start items-start gap-2 w-[100%]">
              <Typography className="reject-application-text">Quick Reasons</Typography>
              <FormControl sx={{ width: '100%' }}>
                <Select
                  value={selectedPresetReason}
                  onChange={handlePresetReasonChange}
                  displayEmpty
                  className="preset-reason-dropdown"
                  IconComponent={KeyboardArrowDownIcon}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                        borderRadius: '8px',
                        marginTop: '4px',
                      },
                    },
                  }}
                  sx={{
                    height: '44px',
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: '1px solid #D5DEE3',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#949DA6',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0A5EF3',
                      borderWidth: '2px',
                    },
                    '& .MuiSelect-select': {
                      padding: '10px 14px',
                      fontFamily: 'Poppins',
                      fontSize: '14px',
                      color: selectedPresetReason ? '#14171F' : '#949DA6',
                    },
                  }}
                >
                  <MenuItem value="" disabled sx={{ color: '#949DA6', fontStyle: 'italic' }}>
                    Select a reason
                  </MenuItem>
                  {REJECTION_REASONS.map((reasonText, index) => (
                    <MenuItem
                      key={index}
                      value={reasonText}
                      sx={{
                        fontFamily: 'Poppins',
                        fontSize: '14px',
                        padding: '10px 16px',
                        '&:hover': {
                          backgroundColor: '#F0F4FF',
                        },
                        '&.Mui-selected': {
                          backgroundColor: '#E9F1FF',
                          '&:hover': {
                            backgroundColor: '#E9F1FF',
                          },
                        },
                      }}
                    >
                      {reasonText}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Custom Reason Text Area */}
            <Box className="flex flex-col justify-start items-start gap-2 w-[100%]">
              <Typography className="reject-application-text">Reason Details</Typography>
              <textarea
                placeholder="Write or modify the reason..."
                className="reject-application-textarea"
                value={reason}
                onChange={handleTextChange}
                rows={3}
              />
            </Box>
          </Box>
        </Box>
        <Box className="flex items-center justify-between gap-4 px-6 py-4">
          <button className="cancel-application-button-updated" onClick={handleClose}>
            Cancel
          </button>
          <button className="reject-application-button-updated" onClick={handleReject}>
            Reject
          </button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SubjectRejectModal;
